/**
 * ResearchDetail — /research/:id
 * RES-1: Project detail page with findings/questions/members/tasks/summary tabs
 * All data fetched via apiFetch (auth header included)
 */
import {
    ArrowLeft,
    BookOpen,
    CheckSquare,
    FileText,
    FlaskConical,
    HelpCircle,
    MessageSquare,
    Play,
    Plus,
    Settings,
    Square,
    Users
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

interface Project {
  id: number
  title: string
  description: string
  category: string
  status: string
  budget_flz: number
  spent_flz: number
  total_findings: number
  total_questions: number
  agent_count: number
  owner_id?: number
  owner_name: string
  created_at: string
}

interface Finding {
  id: number
  title: string
  content: string
  agent_name?: string
  eq_score?: number
  created_at: string
}

interface Question {
  id: number
  question: string
  answer?: string
  status: string
  created_at: string
}

interface Member {
  id: number
  agent_name: string
  role: string
  findings_count: number
  joined_at: string
}

interface Task {
  id: number
  title: string
  status: string
  assigned_to?: string
  created_at: string
}

interface Summary {
  id?: number
  content: string
  generated_at?: string
}

type DetailTab = 'findings' | 'questions' | 'members' | 'tasks' | 'summary' | 'settings'

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    active: { label: 'Active', color: '#10b981' },
    paused: { label: 'Paused', color: '#f59e0b' },
    completed: { label: 'Completed', color: '#3b82f6' },
    planning: { label: 'Planning', color: '#8b5cf6' },
  }
  const s = map[status] || { label: status, color: 'var(--secondary)' }
  return (
    <span style={{
      background: s.color + '22', color: s.color,
      border: `1px solid ${s.color}44`,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600
    }}>{s.label}</span>
  )
}

export default function ResearchDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const projectId = Number(id)

  const [project, setProject] = useState<Project | null>(null)
  const [findings, setFindings] = useState<Finding[]>([])
  const [questions, setQuestions] = useState<Question[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [summaryData, setSummaryData] = useState<{ summary: string | null; hot_topics: string[]; key_findings: string[]; open_questions: string[]; updated_at?: string } | null>(null)
  const [tab, setTab] = useState<DetailTab>('findings')
  const [loading, setLoading] = useState(true)
  const [tabLoading, setTabLoading] = useState(false)
  const [generatingSummary, setGeneratingSummary] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load project details
  useEffect(() => {
    if (!projectId) return
    setLoading(true)
    apiFetch(`${API_URL}/api/research/projects/${projectId}`)
      .then(r => r.json())
      .then((d: any) => setProject(d.project ?? d))
      .catch(() => setError('Failed to load project'))
      .finally(() => setLoading(false))
  }, [projectId])

  // Load tab data
  useEffect(() => {
    if (!projectId) return
    setTabLoading(true)
    const endpoints: Record<DetailTab, string> = {
      findings: `/api/research/projects/${projectId}/findings`,
      questions: `/api/research/projects/${projectId}/questions`,
      members: `/api/research/projects/${projectId}/members`,
      tasks: `/api/research/projects/${projectId}/tasks`,
      summary: `/api/research/projects/${projectId}/summary`,
      settings: `/api/research/projects/${projectId}`,
    }
    apiFetch(`${API_URL}${endpoints[tab]}`)
      .then(r => r.json())
      .then((d: any) => {
        if (tab === 'findings') setFindings(Array.isArray(d) ? d : d.findings ?? [])
        else if (tab === 'questions') setQuestions(Array.isArray(d) ? d : d.questions ?? [])
        else if (tab === 'members') setMembers(Array.isArray(d) ? d : d.members ?? [])
        else if (tab === 'tasks') setTasks(Array.isArray(d) ? d : d.tasks ?? [])
        else if (tab === 'summary') {
          const summaryText = d.summary ?? d.content ?? null
          const hotTopics = d.hot_topics ? (typeof d.hot_topics === 'string' ? JSON.parse(d.hot_topics) : d.hot_topics) : []
          const keyFindings = d.key_findings ? (typeof d.key_findings === 'string' ? JSON.parse(d.key_findings) : d.key_findings) : []
          const openQuestions = d.open_questions ? (typeof d.open_questions === 'string' ? JSON.parse(d.open_questions) : d.open_questions) : []
          setSummaryData({ summary: summaryText, hot_topics: hotTopics, key_findings: keyFindings, open_questions: openQuestions, updated_at: d.updated_at })
        }
      })
      .catch(() => { /* tab data not critical */ })
      .finally(() => setTabLoading(false))
  }, [projectId, tab])

  const handleStart = async () => {
    await apiFetch(`${API_URL}/api/research/projects/${projectId}/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
    const r = await apiFetch(`${API_URL}/api/research/projects/${projectId}`)
    const d = await r.json()
    setProject(d.project ?? d)
  }

  const handleStop = async () => {
    await apiFetch(`${API_URL}/api/research/projects/${projectId}/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
    const r = await apiFetch(`${API_URL}/api/research/projects/${projectId}`)
    const d = await r.json()
    setProject(d.project ?? d)
  }

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true)
    try {
      // Try POST first, fallback to GET if 405
      const genRes = await apiFetch(`${API_URL}/api/research/projects/${projectId}/summary/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      if (!genRes.ok && genRes.status !== 405) { /* ignore */ }
      const r = await apiFetch(`${API_URL}/api/research/projects/${projectId}/summary`)
      const d = await r.json()
      const summaryText = d.summary ?? d.content ?? null
      const hotTopics = d.hot_topics ? (typeof d.hot_topics === 'string' ? JSON.parse(d.hot_topics) : d.hot_topics) : []
      const keyFindings = d.key_findings ? (typeof d.key_findings === 'string' ? JSON.parse(d.key_findings) : d.key_findings) : []
      const openQuestions = d.open_questions ? (typeof d.open_questions === 'string' ? JSON.parse(d.open_questions) : d.open_questions) : []
      setSummaryData({ summary: summaryText, hot_topics: hotTopics, key_findings: keyFindings, open_questions: openQuestions, updated_at: d.updated_at })
    } catch { /* */ }
    setGeneratingSummary(false)
  }

  if (loading) return (
    <MainLayout>
      <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-secondary)' }}>Loading project...</div>
    </MainLayout>
  )

  if (error || !project) return (
    <MainLayout>
      <div style={{ textAlign: 'center', padding: '80px 0', color: '#ef4444' }}>
        {error || 'Project not found'}
        <br />
        <button onClick={() => navigate('/research')} style={{ marginTop: 12, padding: '8px 16px', background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          Back to Research
        </button>
      </div>
    </MainLayout>
  )

  const catColor = '#10b981'
  const budgetUsed = project.budget_flz > 0 ? Math.min(100, Math.round((project.spent_flz / project.budget_flz) * 100)) : 0

  return (
    <MainLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
        {/* Back */}
        <button onClick={() => navigate('/research')} style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
          color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, marginBottom: 20, padding: 0
        }}>
          <ArrowLeft size={15} /> Back to Research
        </button>

        {/* Project header */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 16, padding: 28, marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: catColor }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              {project.category && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: catColor + '18', color: catColor, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, marginBottom: 10 }}>
                  <FlaskConical size={12} /> {project.category}
                </div>
              )}
              <h1 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{project.title}</h1>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>{project.description}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <StatusBadge status={project.status} />
              <div style={{ display: 'flex', gap: 8 }}>
                {project.status !== 'active' && (user?.role === 'admin' || project.owner_id === user?.id) && (
                  <button onClick={handleStart} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <Play size={14} /> Start
                  </button>
                )}
                {project.status === 'active' && (user?.role === 'admin' || project.owner_id === user?.id) && (
                  <button onClick={handleStop} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <Square size={14} /> Stop
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: project.budget_flz > 0 ? 16 : 0 }}>
            {[
              { icon: <FlaskConical size={14} />, value: project.total_findings || 0, label: 'Findings', color: '#10b981' },
              { icon: <HelpCircle size={14} />, value: project.total_questions || 0, label: 'Questions', color: '#6366f1' },
              { icon: <Users size={14} />, value: project.agent_count || 0, label: 'Agents', color: '#f59e0b' },
              { icon: <MessageSquare size={14} />, value: 0, label: 'by ' + (project.owner_name || 'Unknown'), color: '#3b82f6' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--body-bg)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: s.color, marginBottom: 4 }}>
                  {s.icon}
                  <span style={{ fontSize: 18, fontWeight: 700 }}>{s.value}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Budget bar */}
          {project.budget_flz > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Budget</span>
                <span style={{ fontSize: 12, color: catColor, fontWeight: 600 }}>
                  {project.spent_flz.toLocaleString()} / {project.budget_flz.toLocaleString()} FLZ ({budgetUsed}%)
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--border-color)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${budgetUsed}%`, background: catColor, borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: 20 }}>
          {([
            ['findings', 'Findings', FlaskConical],
            ['questions', 'Questions', HelpCircle],
            ['members', 'Members', Users],
            ['tasks', 'Tasks', CheckSquare],
            ['summary', 'Summary', FileText],
            ...(user?.role === 'admin' || project.owner_id === user?.id ? [['settings', 'Settings', Settings] as const] : []),
          ] as const).map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k as DetailTab)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', fontSize: 13,
              fontWeight: tab === k ? 700 : 400, color: tab === k ? '#10b981' : 'var(--text-secondary)',
              background: 'transparent', border: 'none',
              borderBottom: tab === k ? '2px solid #10b981' : '2px solid transparent',
              cursor: 'pointer'
            }}><I size={14} /> {l}</button>
          ))}
        </div>

        {/* Tab content */}
        {tabLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : (
          <>
            {/* Findings */}
            {tab === 'findings' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {findings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                    <FlaskConical size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>No findings yet. Start the research to generate findings.</p>
                  </div>
                ) : findings.map(f => (
                  <div key={f.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{f.title}</h3>
                      {f.eq_score && (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(99,102,241,0.1)', color: '#6366f1', fontWeight: 600 }}>EQ {f.eq_score}</span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>{f.content}</p>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-secondary)' }}>
                      {f.agent_name && <span>Agent: {f.agent_name}</span>}
                      <span>{new Date(f.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Questions */}
            {tab === 'questions' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {questions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                    <HelpCircle size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>No questions yet.</p>
                  </div>
                ) : questions.map(q => (
                  <div key={q.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{q.question}</p>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600, marginLeft: 10,
                        background: q.status === 'answered' ? '#10b98122' : '#f59e0b22',
                        color: q.status === 'answered' ? '#10b981' : '#f59e0b'
                      }}>{q.status}</span>
                    </div>
                    {q.answer && <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, paddingLeft: 12, borderLeft: '2px solid #10b981' }}>{q.answer}</p>}
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Members */}
            {tab === 'members' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {members.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                    <Users size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>No members yet.</p>
                  </div>
                ) : members.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '14px 20px' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700 }}>
                      {(m.agent_name || '?')[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{m.agent_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.role} · {m.findings_count} findings</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Joined {new Date(m.joined_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Tasks */}
            {tab === 'tasks' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tasks.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                    <CheckSquare size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>No tasks yet.</p>
                  </div>
                ) : tasks.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '12px 16px' }}>
                    <CheckSquare size={16} color={t.status === 'completed' ? '#10b981' : 'var(--text-secondary)'} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.title}</div>
                      {t.assigned_to && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Assigned to: {t.assigned_to}</div>}
                    </div>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                      background: t.status === 'completed' ? '#10b98122' : '#f59e0b22',
                      color: t.status === 'completed' ? '#10b981' : '#f59e0b'
                    }}>{t.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {tab === 'summary' && (
              <div>
                {summaryData?.summary || (summaryData?.hot_topics && summaryData.hot_topics.length > 0) ? (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 16, padding: 28 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                        <BookOpen size={16} color="#6366f1" /> Research Summary
                      </div>
                      <button onClick={handleGenerateSummary} disabled={generatingSummary} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8,
                        background: '#6366f1', color: '#fff', border: 'none', cursor: generatingSummary ? 'not-allowed' : 'pointer',
                        fontSize: 12, fontWeight: 600, opacity: generatingSummary ? 0.6 : 1
                      }}>
                        <Plus size={12} /> {generatingSummary ? 'Generating...' : 'Regenerate'}
                      </button>
                    </div>
                    {summaryData.summary && (
                      <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{summaryData.summary}</p>
                    )}
                    {summaryData.hot_topics && summaryData.hot_topics.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Hot Topics</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {summaryData.hot_topics.map((t, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                              <span style={{ color: '#f59e0b', fontWeight: 700, flexShrink: 0 }}>#{i + 1}</span> {t}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {summaryData.key_findings && summaryData.key_findings.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Key Findings</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {summaryData.key_findings.map((f, i) => (
                            <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', paddingLeft: 12, borderLeft: '2px solid #10b981' }}>{f}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {summaryData.updated_at && (
                      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-secondary)' }}>
                        Updated: {new Date(summaryData.updated_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                    <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p style={{ marginBottom: 16 }}>No summary yet. Generate one from the findings.</p>
                    <button onClick={handleGenerateSummary} disabled={generatingSummary} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
                      background: '#6366f1', color: '#fff', border: 'none', cursor: generatingSummary ? 'not-allowed' : 'pointer',
                      fontSize: 14, fontWeight: 600, margin: '0 auto', opacity: generatingSummary ? 0.6 : 1
                    }}>
                      <Plus size={16} /> {generatingSummary ? 'Generating...' : 'Generate Summary'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Settings (admin/owner only) */}
            {tab === 'settings' && (user?.role === 'admin' || project.owner_id === user?.id) && (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 16, padding: 28 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Project Settings</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    <strong>Status:</strong> {project.status}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    <strong>Owner:</strong> {project.owner_name}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    <strong>Created:</strong> {new Date(project.created_at).toLocaleString()}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {project.status === 'active' && (
                      <button onClick={handleStop} style={{ padding: '8px 16px', borderRadius: 8, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        Stop Project
                      </button>
                    )}
                    {project.status !== 'active' && (
                      <button onClick={handleStart} style={{ padding: '8px 16px', borderRadius: 8, background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        Start Project
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  )
}
